/**
 * Time timezone management hook
 * Provides timezone-aware date/time formatting for time entries
 */

import { useCallback, useMemo } from 'react'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { useAuth } from '@/store/auth.store'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'
import { format, parseISO } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

import { resolveTimeZone } from '@scrambl/shared'

export interface TimezoneInfo {
  /** User's timezone (from profile or system) */
  userTimezone: string
  /** Organization's timezone */
  orgTimezone: string
  /** Currently active timezone for display */
  activeTimezone: string
  /** Whether user and org timezones differ */
  timezonesMatch: boolean
  /** Readable label for user timezone */
  userTimezoneLabel: string
  /** Readable label for org timezone */
  orgTimezoneLabel: string
  /** Short abbreviation for active timezone */
  activeTimezoneAbbr: string
}

/**
 * Get a readable timezone label from an IANA timezone string
 * e.g., "America/Los_Angeles" -> "Los Angeles"
 */
function getTimezoneLabel(timezone: string): string {
  const parts = timezone.split('/')
  const city = parts[parts.length - 1]
  return city.replace(/_/g, ' ')
}

/**
 * Get timezone abbreviation for a given timezone and date
 * e.g., "PST" or "PDT" depending on DST
 */
function getTimezoneAbbreviation(
  timezone: string,
  date: Date = new Date(),
): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(date)
    const tzPart = parts.find(part => part.type === 'timeZoneName')
    return tzPart?.value || timezone
  } catch {
    return timezone
  }
}

/**
 * Hook for managing time entry timezone preferences and utilities
 */
export function useTimeTimezone() {
  const { user } = useAuth()
  const { data: settingsData } = useOrganizationSettingsQuery()
  const { timeViewOrgTimezone, setTimeViewOrgTimezone } =
    useUIPreferencesStore()

  // Get user's timezone (profile or system fallback)
  const userTimezone = resolveTimeZone(
    user?.timezone,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  )

  // Get organization's timezone from settings
  const orgTimezone = useMemo(() => {
    return resolveTimeZone(settingsData?.effectiveSettings?.general?.timezone)
  }, [settingsData])

  // Determine if timezones match
  const timezonesMatch = useMemo(() => {
    return userTimezone === orgTimezone
  }, [userTimezone, orgTimezone])

  // Active timezone based on toggle preference
  const activeTimezone = useMemo(() => {
    // If timezones match, just use user timezone
    if (timezonesMatch) return userTimezone
    // Otherwise, use the toggle preference
    return timeViewOrgTimezone ? orgTimezone : userTimezone
  }, [timezonesMatch, timeViewOrgTimezone, orgTimezone, userTimezone])

  // Timezone info object
  const timezoneInfo: TimezoneInfo = useMemo(
    () => ({
      userTimezone,
      orgTimezone,
      activeTimezone,
      timezonesMatch,
      userTimezoneLabel: getTimezoneLabel(userTimezone),
      orgTimezoneLabel: getTimezoneLabel(orgTimezone),
      activeTimezoneAbbr: getTimezoneAbbreviation(activeTimezone),
    }),
    [userTimezone, orgTimezone, activeTimezone, timezonesMatch],
  )

  // Toggle between user and org timezone
  const toggleTimezone = useCallback(() => {
    if (!timezonesMatch) {
      setTimeViewOrgTimezone(!timeViewOrgTimezone)
    }
  }, [timezonesMatch, timeViewOrgTimezone, setTimeViewOrgTimezone])

  // Format a time in the active timezone
  const formatTimeInTimezone = useCallback(
    (date: Date | string, formatStr: string = 'h:mm a'): string => {
      try {
        const dateObj = typeof date === 'string' ? parseISO(date) : date
        return formatInTimeZone(dateObj, activeTimezone, formatStr)
      } catch {
        // Fallback to standard format
        const dateObj = typeof date === 'string' ? new Date(date) : date
        return format(dateObj, formatStr)
      }
    },
    [activeTimezone],
  )

  // Format a date in the active timezone
  const formatDateInTimezone = useCallback(
    (date: Date | string, formatStr: string = 'MMM d, yyyy'): string => {
      try {
        const dateObj = typeof date === 'string' ? parseISO(date) : date
        return formatInTimeZone(dateObj, activeTimezone, formatStr)
      } catch {
        // Fallback to standard format
        const dateObj = typeof date === 'string' ? new Date(date) : date
        return format(dateObj, formatStr)
      }
    },
    [activeTimezone],
  )

  // Get the current time in the active timezone
  const getCurrentTimeInTimezone = useCallback((): Date => {
    return utcToZonedTime(new Date(), activeTimezone)
  }, [activeTimezone])

  // Convert a UTC date to the active timezone
  const toActiveTimezone = useCallback(
    (date: Date): Date => {
      return utcToZonedTime(date, activeTimezone)
    },
    [activeTimezone],
  )

  // Convert from active timezone back to UTC for storage
  const fromActiveTimezone = useCallback(
    (date: Date): Date => {
      return zonedTimeToUtc(date, activeTimezone)
    },
    [activeTimezone],
  )

  // Get timezone offset string (e.g., "+05:30" or "-08:00")
  const getTimezoneOffset = useCallback(
    (timezone: string = activeTimezone): string => {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          timeZoneName: 'longOffset',
        })
        const parts = formatter.formatToParts(new Date())
        const tzPart = parts.find(part => part.type === 'timeZoneName')
        // Extract offset from "GMT+05:30" format
        const match = tzPart?.value.match(/GMT([+-]\d{2}:\d{2})/)
        return match ? match[1] : ''
      } catch {
        return ''
      }
    },
    [activeTimezone],
  )

  return {
    // Timezone info
    ...timezoneInfo,
    timezoneInfo,

    // Toggle function
    toggleTimezone,
    isViewingOrgTimezone: timeViewOrgTimezone,

    // Formatting utilities
    formatTimeInTimezone,
    formatDateInTimezone,
    getCurrentTimeInTimezone,
    toActiveTimezone,
    fromActiveTimezone,
    getTimezoneOffset,

    // Static utilities
    getTimezoneLabel,
    getTimezoneAbbreviation,
  }
}

export type UseTimeTimezoneReturn = ReturnType<typeof useTimeTimezone>

// Re-export with old name for backwards compatibility
/** @deprecated Use useTimeTimezone instead */
export const useTimesheetTimezone = useTimeTimezone
/** @deprecated Use UseTimeTimezoneReturn instead */
export type UseTimesheetTimezoneReturn = UseTimeTimezoneReturn
